/**
 * Member-supplied phone numbers for scraped listings.
 *
 * A listing that came from Google carries source = 'google'. When a member
 * types its number from their own phone and it agrees with what we hold, the
 * number has been independently supplied and the row becomes 'member'. That is
 * the whole mechanism behind 7_OWN_OUR_DATA.md.
 *
 * The decision is made in Postgres, not here: submit_listing_phone() compares
 * against the stored number and returns the outcome. The client never says
 * what happened, only what was typed — otherwise a member could claim a match
 * for a number they never entered.
 */

import supabase from "../config/supabase";

const listingPhonesApi = {
  async confirmationCount(serviceId) {
    if (!serviceId) return {success: false, count: null};
    const {data, error} = await supabase.rpc("listing_phone_confirmation_count", {p_service_id: serviceId});
    if (error || !Number.isSafeInteger(Number(data)) || Number(data) < 0 || data == null) return {success: false, count: null};
    return {success: true, count: Number(data)};
  },
  /**
   * Submit a number a member has read off their own phone.
   *
   * Returns { success, outcome } where outcome is:
   *   added    — we held nothing, theirs is now the number
   *   matched  — agrees with ours; the listing is no longer Google-sourced
   *   mismatch — disagrees; recorded for an admin, listing left alone
   */
  async submit(serviceId, phone) {
    if (!serviceId) return { success: false, error: "No listing given." };

    const { data, error } = await supabase.rpc("submit_listing_phone", {
      p_service_id: serviceId,
      p_phone: phone,
    });

    if (error) return { success: false, error: error.message };
    return { success: true, outcome: data?.outcome };
  },

  /**
   * What this member has already said about this listing, so the screen can
   * show "you've already confirmed this" instead of asking twice.
   */
  async getMine(serviceId) {
    if (!serviceId) return { success: true, data: null };

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: true, data: null };

    const { data, error } = await supabase
      .from("listing_phone_submissions")
      .select("outcome, submitted, created_at")
      .eq("service_id", serviceId)
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },
};

export default listingPhonesApi;
